import React, { Fragment, ReactNode } from 'react';

import { useLink } from '@zapier/common-hooks';
import { Button, Link } from '@zapier/design-system';
import { Price } from '@zapier/currency';

import { UpgradeButton } from '@zapier/design-system-revenue/src/components/offers/UpgradeModal/UpgradeReason/UpgradeButton';
import { Interval } from '@zapier/design-system-revenue/src/types';
import { buildUpgradeUrl } from '@zapier/design-system-revenue/src/utils';

import { Props as TaskLimitUpgradeNudgeProps } from '../TaskLimitUpgradeNudge';

const monthlyAmount = (amount: number, interval: Interval) =>
  interval === 'year' ? amount / 12 : amount;

const HeldTasksButton = ({ onClick }: { onClick?: () => void }) => {
  const { routeTo } = useLink();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }

    if (routeTo) {
      routeTo('/app/zaps/folder/held');
    }
  };

  return (
    <Button color="greyAlt" key="see-held-tasks" onClick={handleClick}>
      See Held Tasks
    </Button>
  );
};

const TaskLimitUpgradeButton = ({
  currentPlanInterval,
  currentPlanName,
  nextPlanName,
  onClick,
}: {
  currentPlanInterval: Interval;
  currentPlanName: string;
  nextPlanName: string;
  onClick?: () => void;
}) => {
  const { routeTo } = useLink();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }

    if (routeTo) {
      routeTo(
        buildUpgradeUrl(currentPlanName, nextPlanName, currentPlanInterval)
      );
    }
  };

  return <UpgradeButton onClick={handleClick} />;
};

const baseBodyText = (
  <Fragment>
    You've run out of available Tasks on your account, and{' '}
    <Link href="/help/manage/tasks/learn-about-tasks-in-zapier" target="_blank">
      your Tasks are held
    </Link>
    .
  </Fragment>
);

type ViewType =
  | 'LEAVING_FREE'
  | 'LEAVING_FREE_OR_STARTING_TRIAL'
  | 'NEW_PLAN_TYPE'
  | 'NEW_TASK_LIMIT_SAME_PLAN_TYPE';

type View = {
  bodyText: ReactNode;
  buttons: ReactNode[];
  type: ViewType;
};

/**
 * Note: Upgrade buttons in these views take the user to the upgrade
 * page rather than doing the upgrade inline because these nudges could
 * be used in `web-ssr`, and the payment app doesn't work in `web-ssr`.
 * This also gives the user the chance to change their billing interval,
 * which is common for annual users who need to upgrade to the next
 * task limit just to get them through the rest of the month.
 */
const views: {
  [key in ViewType]: (...args: any[]) => View;
} = {
  ['LEAVING_FREE']: ({ currentPlan, nextPlan }) => ({
    bodyText: (
      <Fragment>
        {baseBodyText} Upgrade your plan now to get{' '}
        {Number(nextPlan.tasks).toLocaleString()} monthly Tasks for{' '}
        <Price>{monthlyAmount(nextPlan.amount, nextPlan.interval)}</Price>/mo.{' '}
      </Fragment>
    ),
    buttons: [
      <TaskLimitUpgradeButton
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        nextPlanName={nextPlan.name}
      />,

      <HeldTasksButton />,
    ],
    type: 'LEAVING_FREE',
  }),

  ['LEAVING_FREE_OR_STARTING_TRIAL']: ({
    currentPlan,
    extendTrialButton,
    nextPlan,
  }) => {
    // Trial extensions require the Payment app, which doesn't work in ssr environments.
    const isBrowser = typeof window !== undefined;

    const buttons = [
      <TaskLimitUpgradeButton
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        nextPlanName={nextPlan.name}
      />,
    ];

    if (isBrowser) {
      buttons.push(extendTrialButton);
    }

    return {
      bodyText: (
        <Fragment>
          {baseBodyText} Upgrade your plan now to get{' '}
          {Number(nextPlan.tasks).toLocaleString()} monthly Tasks for{' '}
          <Price>{monthlyAmount(nextPlan.amount, nextPlan.interval)}</Price>/mo
          {isBrowser && (
            <span> or start a free 7-day trial of Zapier Professional</span>
          )}
          .
        </Fragment>
      ),
      buttons,
      type: 'LEAVING_FREE_OR_STARTING_TRIAL',
    };
  },

  ['NEW_PLAN_TYPE']: ({ currentPlan, nextPlan }) => ({
    bodyText: (
      <Fragment>
        {baseBodyText} Upgrade to a Zapier {nextPlan.type} account now to get{' '}
        {Number(nextPlan.tasks).toLocaleString()} monthly Tasks for{' '}
        <Price>
          {monthlyAmount(nextPlan.amount, nextPlan.interval) -
            monthlyAmount(currentPlan.amount, currentPlan.interval)}
        </Price>{' '}
        more per month.
      </Fragment>
    ),
    buttons: [
      <TaskLimitUpgradeButton
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        nextPlanName={nextPlan.name}
      />,

      <HeldTasksButton />,
    ],
    type: 'NEW_PLAN_TYPE',
  }),

  ['NEW_TASK_LIMIT_SAME_PLAN_TYPE']: ({ currentPlan, nextPlan }) => ({
    bodyText: (
      <Fragment>
        {baseBodyText} Upgrade your plan now to get{' '}
        {Number(nextPlan.tasks).toLocaleString()} monthly Tasks for{' '}
        <Price>
          {monthlyAmount(nextPlan.amount, nextPlan.interval) -
            monthlyAmount(currentPlan.amount, currentPlan.interval)}
        </Price>{' '}
        more per month.
      </Fragment>
    ),
    buttons: [
      <TaskLimitUpgradeButton
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        nextPlanName={nextPlan.name}
      />,

      <HeldTasksButton />,
    ],
    type: 'NEW_TASK_LIMIT_SAME_PLAN_TYPE',
  }),
};

export const getActiveView = ({
  activeTrialType,
  canExtendTrial,
  currentPlan,
  extendTrialButton,
  lowestTeamOrCompanyPlan,
  nextPlan,
}: Omit<TaskLimitUpgradeNudgeProps, 'onClose'>) => {
  switch (activeTrialType) {
    case 'Professional': {
      return views.LEAVING_FREE({
        currentPlan,
        nextPlan,
      });
    }

    case 'Team':
    case 'Company': {
      return views.NEW_PLAN_TYPE({
        currentPlan,
        nextPlan: lowestTeamOrCompanyPlan,
      });
    }

    default: {
      if (currentPlan.amount === 0) {
        return canExtendTrial
          ? views.LEAVING_FREE_OR_STARTING_TRIAL({
              currentPlan,
              extendTrialButton,
              nextPlan,
            })
          : views.LEAVING_FREE({
              currentPlan,
              nextPlan,
            });
      }

      return currentPlan.type === nextPlan.type
        ? views.NEW_TASK_LIMIT_SAME_PLAN_TYPE({
            currentPlan,
            nextPlan,
          })
        : views.NEW_PLAN_TYPE({
            currentPlan,
            nextPlan,
          });
    }
  }
};
